import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { routeRoles, RouteRole } from './route-roles';

// AWS Amplify Auth
const Auth = require('@aws-amplify/auth').default;

@Injectable({
  providedIn: 'root'
})
export class TokenRouteGuardService implements CanActivate {

  constructor(
    private _router: Router
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return Auth.currentAuthenticatedUser()
      .then((user) => {
        // Extract roles from token
        const idToken = user.signInUserSession.idToken.jwtToken;
        const payload = this.parseJwt(idToken);
        const userRoles = payload['dentsu:roles'] || [];

        // Get current URL path
        const url = state.url;

        // Check if user has permission to access this route based on roles
        if (this.checkPermissions(url, userRoles)) {
          return true;
        } else {
          this._router.navigate(['/dashboard']);
          return false;
        }
      })
      .catch((error) => {
        this._router.navigate(['/landing/login']);
        return false;
      });
  }

  /**
   * Parse JWT token to get payload
   */
  private parseJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  /**
   * Check if user has permission to access the route based on roles
   */
  private checkPermissions(url: string, userRoles: string[]): boolean {
    const matchingRouteRole = this.findRouteRoleByUrl(url);

    if (!matchingRouteRole) {
      return false;
    }

    // Check if the user has any of the required roles
    const hasRequiredRole = matchingRouteRole.roles.some(role =>
      userRoles.includes(role.toString())
    );
    return hasRequiredRole;
  }

  /**
   * Find route role by URL
   */
  private findRouteRoleByUrl(url: string): RouteRole | null {
    const normalizedUrl = url.startsWith('/') ? url.substring(1) : url;

    // Try exact match first
    let matchingRole = routeRoles.find(role => {
      const roleUri = role.uri.startsWith('/') ? role.uri.substring(1) : role.uri;
      return roleUri === normalizedUrl;
    });

    if (matchingRole) {
      return matchingRole;
    }

    // Try partial match (parent routes)
    matchingRole = routeRoles.find(role => {
      const roleUri = role.uri.startsWith('/') ? role.uri.substring(1) : role.uri;
      return normalizedUrl.startsWith(roleUri + '/');
    });

    // If no exact or parent match, try to match by path parts
    if (!matchingRole) {
      const urlParts = normalizedUrl.split('/');

      for (let i = urlParts.length - 1; i > 0; i--) {
        const partialUrl = urlParts.slice(0, i).join('/');

        matchingRole = routeRoles.find(role => {
          const roleUri = role.uri.startsWith('/') ? role.uri.substring(1) : role.uri;
          return roleUri === partialUrl;
        });

        if (matchingRole) {
          break;
        }
      }
    }

    return matchingRole || null;
  }
}
